<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import pexelLogo from '@consumer/assets/images/pexels.png'
import type { Photo, UploadedPhoto } from '@consumer/types/Photo'
import type { GiftlyDesign } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { useThrottleFn } from '@vueuse/core'
import { UNSPLASH_ATTRIBUTION_UTM } from '@consumer/constants/unsplash'
import { last, sortByCondition } from '@consumer/helpers/array'
import { v3WithImageBg } from '@consumer/logic/giftly'
import {
  getRecommendedDesigns,
  canViewMore,
  searchDesigns,
  useGiftlyDesigns,
  type DesignSearchResults,
} from '@consumer/services/api/customizer'
import { speedbumpText } from '@consumer/constants/disclaimers'
import { faSearch } from '@fortawesome/pro-light-svg-icons'
import newMiscUnsplashIcon from '@consumer/assets/icons/new-misc/unsplash.svg?svgicon'
import ImageUpload from './stepDesign/ImageUpload.vue'

const suggestedSearchTerms = [
  'Coffee',
  'Spa',
  'Brunch',
  'Restaurants',
  'Tools',
  'Lunch',
  'Massage',
  'Cafes',
  'Bouquet',
  'Confetti',
  'Fuchsia',
  'Stripes',
  'Gold',
]

withDefaults(defineProps<{ gift: GiftTemplate; allowBackgroundUpload?: boolean }>(), { allowBackgroundUpload: false, })

const emit = defineEmits<{ stepComplete: []; newImage: [Photo | GiftlyDesign] }>()

let isLoading = _ref(false)
let searchError = _ref<string>()
let searchTerm = _ref<string>()
const searchResults = reactive<DesignSearchResults | Partial<DesignSearchResults>>({})

const initialDesign = _ref<GiftlyDesign | undefined>(
  !__props.gift.giftlyMetadata?.designName
    || (
      __props.gift.giftlyMetadata?.designName === v3WithImageBg
      && (!__props.allowBackgroundUpload || __props.gift.giftlyMetadata.unsplashImageId)
    )
    ? undefined
    : {
      ...__props.gift.giftlyMetadata,
      id: __props.gift.giftlyMetadata?.designName,
      provider: 'giftly',
    })

const giftlyDesigns = (useGiftlyDesigns())
const selectableGiftlyDesigns = _computed(() => {
  if (!initialDesign.value) return giftlyDesigns.value
  if (giftlyDesigns.value?.some(design => design.id === initialDesign.value?.id)) return giftlyDesigns.value
  return [initialDesign.value, ...(giftlyDesigns.value || [])]
})

let recommendedDesignsPromise: Promise<any> | undefined

async function loadRecommendedDesigns () {
  if (!recommendedDesignsPromise) {
    recommendedDesignsPromise = getRecommendedDesigns()
  }
  Object.assign(searchResults, await recommendedDesignsPromise)
  isLoading.value = false
}

async function onSearchError () {
  searchError.value = `We're running into an issue searching for designs to match ${searchTerm.value}. `
    + 'Here are some recommended designs that you can try in the meantime.'
  await loadRecommendedDesigns()
}

onActivated(() => {
  if (searchResults?.items) {
    searchResults.items = sortDesigns(searchResults.items)
  }
})

// NOTE: Displays the selected image first.
function sortDesigns (designs: Photo[]) {
  const unsplashImageId = __props.gift.giftlyMetadata?.unsplashImageId
  if (!unsplashImageId) { return designs }

  return sortByCondition(designs, photo => photo.id === unsplashImageId)
}

async function searchDesignsBy (query?: string) {
  searchTerm.value = query
  if (!searchTerm.value?.trim()) {
    searchError.value = 'Please enter a search term to find images.'
    return
  }

  isLoading.value = true

  __props.gift.giftlyMetadata ||= {}
  __props.gift.giftlyMetadata.designSearchTerms ||= []

  if (last(__props.gift.giftlyMetadata.designSearchTerms) !== searchTerm.value) {
    __props.gift.giftlyMetadata.designSearchTerms.push(searchTerm.value)
  }

  try {
    const data = await searchDesigns({ searchTerms: searchTerm.value, page: 1, pageSize })
    data.items = sortDesigns(data.items)
    Object.assign(searchResults, data)
    isLoading.value = false
    searchError.value = undefined
  }
  catch {
    await onSearchError()
  }
}

const throttleSearchDesignsBy = useThrottleFn(searchDesignsBy, 1000)

onMounted(() => {
  const searchTerm = last(__props.gift.giftlyMetadata?.designSearchTerms)
  if (searchTerm) {
    searchDesignsBy(searchTerm)
  }
  else {
    loadRecommendedDesigns()
  }
})

const pageSize = 36
function loadMoreDesigns () {
  isLoading.value = true
  const nextPage = (searchResults.page ?? 1) + 1
  searchDesigns({ searchTerms: searchTerm.value ?? '', page: nextPage, pageSize }).then((data) => {
    const existingIds = new Set((searchResults.items ?? []).map(p => p.id))
    data.items = [...(searchResults.items ?? []), ...data.items.filter(p => !existingIds.has(p.id))]
    Object.assign(searchResults, data)
    isLoading.value = false
  }).catch(onSearchError)
}

async function clearSearch () {
  searchTerm.value = undefined
  await loadRecommendedDesigns()
}

function selectImage (photo: Photo | GiftlyDesign) {
  __props.gift.giftlyMetadata ||= {}
  __props.gift.giftlyMetadata.userSelectedImage = true

  emit('newImage', photo)
  emit('stepComplete')
}

function isDesignSelected (photo: Photo | GiftlyDesign) {
  return ('imageUrl' in photo && photo.imageUrl && __props.gift.giftlyMetadata?.backgroundImageUrl === photo.imageUrl)
    || __props.gift.giftlyMetadata?.designName === photo.id
    || __props.gift.giftlyMetadata?.unsplashImageId === photo.id
}

const uploadedImages = ref<UploadedPhoto[]>([])

function imageUploaded (image: UploadedPhoto) {
  uploadedImages.value.push(image)
  selectImage(image)
}

function openUnsplash () {
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(`https://unsplash.com/?${UNSPLASH_ATTRIBUTION_UTM}`, '_blank')
  }
}
</script>

<template>
  <div class="w-full">
    <div class="z-dialog">
      <GMessage
        v-if="searchError"
        v-model:text="searchError"
        class="mb-8 !bg-white bg-opacity-100 z-dialog"
        hideable
        type="error"
        :autoCloseAfter="10000"
      />
    </div>
    <form
      class="w-full flex gap-3 items-center pb-5"
      @submit.prevent="throttleSearchDesignsBy(searchTerm)"
    >
      <div class="flex-grow">
        <GTextInput
          v-model="searchTerm"
          name="nc_design_search_input"
          placeholder="Search Image"
          enterkeyhint="search"
          data-testid="design-search-input"
        >
          <template #addonRight>
            <ClearTextButton v-show="searchTerm" name="nc_design_clear_search" @click="clearSearch"/>
          </template>
        </GTextInput>
      </div>
      <GButton
        primary
        size="large"
        name="nc_design_search"
        type="submit"
        class="g-button-no-label"
      >
        <GIcon :icon="faSearch" size="md"/>
        <span class="hidden">{{ searchTerm }}</span>
      </GButton>
    </form>
    <div
      v-if="!isLoading && (!searchTerm || !searchResults.items?.length)"
      class="flex lg:flex-wrap gap-1 mb-5 overflow-x-auto text-v2-tertiary text-sm"
    >
      <GButton
        v-for="presetQuery, i in suggestedSearchTerms"
        :key="i"
        secondary
        class="g-button-tab px-4 flex-shrink-0 !shadow-none design-search-preset"
        name="nc_design_search_preset"
        :label="presetQuery"
        style="padding: 0 1rem;"
        size="small"
        @click="searchDesignsBy(presetQuery)"
      />
    </div>
    <div v-if="!searchTerm" class="grid grid-cols-2 md:grid-cols-3 gap-4 relative mb-6">
      <template v-if="allowBackgroundUpload">
        <DesignPhoto
          v-for="photo, i in uploadedImages"
          :key="i"
          :photo="photo"
          :selected="isDesignSelected(photo)"
          @select="selectImage"
        />
        <ImageUpload
          @setImage="imageUploaded"
        />
      </template>
      <DesignPhoto
        v-for="design in selectableGiftlyDesigns"
        :key="design.id"
        :photo="design"
        :selected="isDesignSelected(design)"
        @select="selectImage"
      />
    </div>
    <div ref="searchResultsEl">
      <div v-if="searchResults.items" class="space-y-4">
        <div class="text-grey-700 text-sm flex items-center justify-between">
          <div v-if="isLoading" class="flex items-center gap-2">
            <LoadingIndicator size="md" class="text-v2-secondary"/>
            Loading...
          </div>
          <span v-else-if="searchTerm">
            <!-- {{ searchResults.total ? formatNumber(searchResults.total) : 'No' }} Results -->
          </span>
          <span v-else>
            Popular Images
          </span>
          <div class="flex items-center gap-1">
            Powered by
            <a
              v-if="searchResults.provider === 'unsplash'"
              target="_blank"
              :href="`https://unsplash.com/?${UNSPLASH_ATTRIBUTION_UTM}`"
              class="font-semibold"
              @click.prevent="openUnsplash"
            >
              <GIcon :svg="newMiscUnsplashIcon" class="!w-70px !h-5"/>
            </a>
            <a
              v-else
              target="_blank"
              href="https://www.pexels.com"
              class="font-semibold"
            >
              <img class="!h-5" :src="pexelLogo">
            </a>
          </div>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 relative">
          <DesignPhoto
            v-for="photo, i in searchResults.items"
            :key="i"
            :photo="photo"
            :selected="isDesignSelected(photo)"
            @select="selectImage"
          />
        </div>
        <div v-if="isLoading" class="flex items-center justify-center gap-2 h-10">
          <LoadingIndicator size="lg" class="text-v2-secondary"/>
          Loading...
        </div>
        <div
          v-else-if="canViewMore(searchResults)"
          class="flex justify-center"
        >
          <GButton
            secondary
            size="small"
            name="nc_design_view_more"
            label="View More"
            class="w-full"
            data-testid="view-more-designs-button"
            @click="loadMoreDesigns"
          />
        </div>
      </div>
      <div v-else-if="isLoading" class="flex items-center justify-center gap-2 h-10">
        <LoadingIndicator size="lg" class="text-v2-secondary"/>
        Loading...
      </div>
    </div>
  </div>
</template>
